import { ApiError } from '@/lib/api';
import type { ConsentChoicesCatalogue, ConsentChoicesRequest, ConsentChoicesReceipt } from '@/lib/types';

export type ConsentCommand = { body: ConsentChoicesRequest; uncertain: boolean; pending: boolean; receipt?: ConsentChoicesReceipt };
// Same-tab mounted/unmounted recovery; never serialize invitation tokens or
// consent commands into persistent browser storage or diagnostic output.
const commands = new Map<string, ConsentCommand>();
const listeners = new Set<() => void>();
let revision = 0;
export const consentCommandRevision = () => revision;
export const subscribeConsentCommands = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export function notifyConsentCommands() { revision += 1; for (const listener of listeners) listener(); }
export const rememberedCommand = (owner: string) => commands.get(owner);
export function freezeChoices(owner: string, catalogue: ConsentChoicesCatalogue, keys: Set<string>, invitationToken?: string, legacyFallback?: ConsentChoicesRequest['legacy_fallback']): ConsentCommand {
  const existing = commands.get(owner);
  if (existing && !existing.receipt) return existing;
  const selections = catalogue.rows.filter(row => keys.has(row.key) && row.available).map(row => ({ key: row.key, notice_version: row.notice_version, binding_revision: row.binding_revision }));
  if (!selections.length && !invitationToken) throw new Error('Choose at least one consent');
  const command: ConsentCommand = { body: Object.freeze({ operation_id: crypto.randomUUID(), catalogue_revision: catalogue.revision,
    invitation_token: invitationToken ?? null, legacy_fallback: legacyFallback ? Object.freeze({ ...legacyFallback, player_ids: Object.freeze([...legacyFallback.player_ids]) as string[] }) : null, selections: Object.freeze(selections.map(selection => Object.freeze(selection))) as typeof selections }), uncertain: false, pending: false };
  commands.set(owner, command);
  notifyConsentCommands();
  return command;
}
export function rejectedChoices(owner: string, command: ConsentCommand, error: unknown) {
  const known = error instanceof ApiError && [400, 403, 404, 409, 410, 422].includes(error.status);
  if (!known || command.uncertain) command.uncertain = true;
  else if (commands.get(owner) === command) commands.delete(owner);
  notifyConsentCommands();
}
export function validateConsentReceipt(command: ConsentCommand, value: ConsentChoicesReceipt): ConsentChoicesReceipt {
  const expected = command.body.selections.map(row => row.key).sort();
  const actual = value.outcomes?.map(row => row.key).sort();
  if (value.version !== 'consent-choices-accepted.v1' || value.operation_id !== command.body.operation_id
    || value.joined !== !!command.body.invitation_token || JSON.stringify(actual) !== JSON.stringify(expected)
    || value.outcomes.some(row => !['saved', 'not_confirmed'].includes(row.status))
    || (!value.joined && value.destination !== null)
    || (value.joined && !['/feedback', '/coach', '/admin/people', '/'].includes(value.destination ?? ''))) throw new Error('Invalid consent receipt');
  return value;
}
